import { useCallback, useState, BaseSyntheticEvent } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Loader } from "../../components/ui/Loader";
import { TableHeader } from "../../components/ui/table-header";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { AssetType } from "../../queries/models/asset-type.model";
import { useTranslation } from "react-i18next";

interface AssetTypesOverviewProps {
  items: AssetType[] | undefined;
  isLoading: boolean;
  selectedAssetType: AssetType | undefined;
  setSelectedAssetType: (AssetType: AssetType | undefined) => void;
}

export function AssetTypesOverview({
  items,
  isLoading,
  selectedAssetType,
  setSelectedAssetType,
}: AssetTypesOverviewProps) {
  const { t } = useTranslation();
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const colorRowDataTemplate = useCallback(
    (color: string) => (
      <div
        style={{
          backgroundColor: color,
          borderRadius: ".7rem",
          border: "1px solid black",
          height: "1.3rem",
          width: "5rem",
        }}
      ></div>
    ),
    []
  );

  const onGlobalFilterChange = useCallback(
    (e: BaseSyntheticEvent) => {
      let _filters = { ...filters };
      const value = e.target.value;

      _filters["global"].value = value;
      setFilters(_filters);
      setGlobalFilterValue(value);
    },
    [filters]
  );

  return (
    <div className="h-full">
      {!isLoading ? (
        <div className="h-full">
          <div className="h-3rem py-1 px-2">
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              className="w-full"
              placeholder="Filter"
            />
          </div>
          <DataTable
            value={items}
            className="AssetTypes-table"
            style={{ height: "calc(100% - 3rem)" }}
            header={
              <TableHeader
                header={t("common.assetTypes")}
                showButton
                buttonLabel={t("common.add")}
                onClick={() => {
                  setSelectedAssetType({ name: "" } as AssetType);
                }}
              />
            }
            scrollable
            scrollHeight="flex"
            selectionMode="single"
            selection={selectedAssetType}
            onSelectionChange={(e) =>
              setSelectedAssetType(e.value as AssetType)
            }
            globalFilterFields={["name", "icon"]}
            filters={filters}
            emptyMessage={t("common.noResultsFound")}
          >
            <Column
              header={t("common.name")}
              field="name"
              sortable
            />
            <Column
              header={t("common.textColor")}
              field="textColor"
              body={(x) => colorRowDataTemplate(x.textColor)}
            />
            <Column
              header={t("common.backgroundColor")}
              field="backgroundColor"
              body={(x) => colorRowDataTemplate(x.backgroundColor)}
            />
            <Column
              header={t("common.icon")}
              field="icon"
              body={(x) =>
                x.icon && (
                  <FontAwesomeIcon
                    icon={x.icon}
                    size="2x"
                  />
                )
              }
            />
          </DataTable>
        </div>
      ) : (
        <div className="h-full flex justify-content-center align-items-center">
          <Loader type="spinner" />
        </div>
      )}
    </div>
  );
}
